import React, { useState, useEffect, useCallback, useMemo } from 'react';
import SharedHeader from '../../../components/shared/SharedHeader';
import LoadingOverlay from '../../../components/shared/LoadingOverlay';
import RecodeLabelForm from './RecodeLabelForm';
import { apiClient } from '../../../api/apiClient';
import { 
  GET_RECORD_LABEL_SONGS_URL, 
  UPDATE_RECORD_LABEL_URL 
} from '../../../api/recordLabel';
import { toast } from '../../../components/shared/toast';
import EmptyState from '../../../components/shared/EmptyState';
import Loader from '../../../components/shared/Loader';
import LabelAvatarStack from '../../../components/shared/LabelAvatarStack';
import './LabelDetails.css';

const TABS = [
  { id: 'basic', labelKey: 'recodeLabels.details.tabBasicDetails', labelDefault: 'Basic Details' },
  { id: 'songs', labelKey: 'recodeLabels.details.tabSongs', labelDefault: 'Songs' },
  { id: 'cases_notes', labelKey: 'recodeLabels.details.tabCasesNotes', labelDefault: 'Cases and Notes' }
];

function LabelDetails({ labelItem, onBack, onInactivate, onActivate, onDelete, onUpdateSuccess, onSelectSong, t }) {
  const [activeTab, setActiveTab] = useState('basic');
  const formatTranslation = (key, params = {}) => {
    const value = typeof t === 'function' ? t(key, params) : key;
    if (typeof value === 'string' && Object.keys(params).length) {
      return value.replace(/\{(\w+)\}/g, (_, field) => params[field] ?? '');
    }
    return value;
  };
  const [isEditing, setIsEditing] = useState(false);

  // Songs tab lazy loading state
  const [songs, setSongs] = useState([]);
  const [songsTotalCount, setSongsTotalCount] = useState(0);
  const [songsCurrentPage, setSongsCurrentPage] = useState(1);
  const [hasMoreSongs, setHasMoreSongs] = useState(true);
  const [isSongsLoadingMore, setIsSongsLoadingMore] = useState(false);
  const [songSearchQuery, setSongSearchQuery] = useState('');
  const [isSongsLoading, setIsSongsLoading] = useState(false);
  const [isExportingSongs, setIsExportingSongs] = useState(false);

  const isActive = labelItem.status === 'Active' || labelItem.status === 1 || labelItem.statusCode === 1;

  // Lazy load songs when Songs tab is active
  const fetchLabelSongs = useCallback(async (pageNum = 1, append = false) => {
    if (activeTab !== 'songs') return;
    try {
      if (append) setIsSongsLoadingMore(true);
      else setIsSongsLoading(true);

      const url = `${GET_RECORD_LABEL_SONGS_URL(labelItem.id)}?page=${pageNum}&limit=20`;
      const response = await apiClient(url);
      if (response && response.songs) {
        setSongs(prev => append ? [...prev, ...response.songs] : (response.songs || []));
        setSongsTotalCount(response.totalCount || 0);
        setHasMoreSongs(response.songs.length === 20);
      }
    } catch (err) {
      console.error('Failed to fetch record label songs:', err);
      if (!append) {
        setSongs([]);
        setSongsTotalCount(0);
      }
    } finally {
      setIsSongsLoading(false);
      setIsSongsLoadingMore(false);
    }
  }, [labelItem.id, activeTab]);

  useEffect(() => {
    if (activeTab === 'songs') {
      setSongsCurrentPage(1);
      fetchLabelSongs(1, false);
    }
  }, [activeTab, labelItem.id]);

  const handleSongsScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    if (scrollHeight - scrollTop <= clientHeight + 50 && hasMoreSongs && !isSongsLoadingMore && !isSongsLoading) {
      const nextPage = songsCurrentPage + 1;
      setSongsCurrentPage(nextPage);
      fetchLabelSongs(nextPage, true);
    }
  };

  // Client-side filter fallback for search within tab
  const filteredSongs = useMemo(() => {
    if (!songSearchQuery.trim()) return songs;
    const q = songSearchQuery.toLowerCase();
    return songs.filter(s => 
      s.name.toLowerCase().includes(q) || 
      s.artist.toLowerCase().includes(q)
    );
  }, [songs, songSearchQuery]);

  const handleExportSongs = async () => {
    try {
      setIsExportingSongs(true);
      await new Promise(resolve => setTimeout(resolve, 800));

      const response = await apiClient(`${GET_RECORD_LABEL_SONGS_URL(labelItem.id)}?export=true`);
      if (response && response.songs) {
        const headers = ['Song Name', 'Sinhala Name', 'Status', 'Artist', 'Lyrics', 'Music', 'Release Date', 'ISRC Code', 'Ownership'];
        const rows = response.songs.map(s => [
          s.name,
          s.nameSinhala || '',
          s.status,
          s.artist,
          s.lyrics,
          s.music,
          s.releaseDate,
          s.isrcCode,
          `${s.ownership}%`
        ]);

        const csvContent = [
          headers.join(','),
          ...rows.map(row => row.map(val => `"${String(val).replace(/"/g, '""')}"`).join(','))
        ].join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.setAttribute('href', url);
        const formattedName = (labelItem.name || 'record_label').replace(/\s+/g, '_');
        link.setAttribute('download', `record_label_songs_${formattedName}_${new Date().toISOString().slice(0, 10)}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        toast.success('Record label songs exported successfully!');
      } else {
        toast.error('Failed to export songs.');
      }
    } catch (err) {
      console.error('Export error:', err);
      toast.error('Failed to export songs list.');
    } finally {
      setIsExportingSongs(false);
    }
  };

  const handleSaveEdit = async (updatedData) => {
    try {
      const response = await apiClient(UPDATE_RECORD_LABEL_URL(labelItem.id), {
        method: 'PUT',
        body: updatedData
      });
      if (response) {
        toast.success('Record label updated successfully!');
        setIsEditing(false);
        if (onUpdateSuccess) onUpdateSuccess();
      }
    } catch (err) {
      console.error('Failed to update record label:', err);
      toast.error(err.message || 'Failed to update record label');
    }
  };

  if (isEditing) {
    return (
      <RecodeLabelForm 
        existingLabel={labelItem}
        onCancel={() => setIsEditing(false)}
        onSave={handleSaveEdit}
        t={t}
      />
    );
  }

  const totalPages = Math.ceil(songsTotalCount / 20) || 1;

  return (
    <div className="label-details-wrapper">
      {/* Navigation path breadcrumbs */}
      <div className="breadcrumb-nav" style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px' }}>
        <button 
          type="button" 
          className="breadcrumb-link" 
          onClick={onBack}
          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: 'var(--text-muted, #64748b)', fontWeight: '500' }}
        >
          {t('recodeLabels.breadcrumb') || 'Record Labels'}
        </button>
        <span className="breadcrumb-separator" style={{ color: '#cbd5e1' }}>&gt;</span>
        <span className="breadcrumb-current" style={{ color: 'var(--text-main, #0f172a)', fontWeight: '600' }}>{labelItem.name}</span>
      </div>

      {/* Top Banner Card displaying logo, name and status */}
      <div className="label-details-card" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#ffffff', borderRadius: '16px', border: '1px solid var(--border-color, #e2e8f0)', padding: '24px', boxShadow: 'var(--card-shadow, 0 1px 3px rgba(0,0,0,0.05))' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <div style={{ width: '56px', height: '56px', borderRadius: '12px', overflow: 'hidden', border: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f8fafc' }}>
            {labelItem.imageUrl ? (
              <img src={labelItem.imageUrl} alt={labelItem.name} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
            ) : (
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2">
                <circle cx="12" cy="12" r="10" />
                <path d="M12 6v12" />
                <path d="M6 12h12" />
              </svg>
            )}
          </div>
          <h1 className="label-banner-name" style={{ fontSize: '24px', fontWeight: '700', color: '#0f172a', margin: 0 }}>{labelItem.name}</h1>
        </div>
        <span className={`status-pill ${isActive ? 'active' : 'inactive'}`}>
          <span className="status-dot"></span>
          {isActive ? 'Active' : 'Inactive'}
        </span>
      </div>

      {/* Tab Selectors Row */}
      <div className="details-tab-nav">
        {TABS.map(tab => (
          <button
            key={tab.id}
            type="button"
            className={`details-tab-btn ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            {t(tab.labelKey) || tab.labelDefault}
          </button>
        ))}
      </div>

      {/* Basic Details tab content panel (Styled matching Song Basic Details UI) */}
      {activeTab === 'basic' && (
        <div className="tab-panel-container" style={{ background: '#ffffff', borderRadius: '16px', border: '1px solid var(--border-color, #e2e8f0)', padding: '24px', boxShadow: 'var(--card-shadow, 0 1px 3px rgba(0,0,0,0.05))' }}>
          <div className="info-rows-list">
            {/* Record Label Name Row */}
            <div className="info-row">
              <div className="info-icon-wrapper">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" />
                  <line x1="7" y1="7" x2="7.01" y2="7" />
                </svg>
              </div>
              <span className="info-label">{t('recodeLabels.details.labelNameLabel') || 'Record Label Name'}</span>
              <span className="info-separator">:</span>
              <span className="info-value">{labelItem.name}</span>
            </div>

            {/* Status Row */}
            <div className="info-row">
              <div className="info-icon-wrapper">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10" />
                  <polyline points="12 6 12 12 16 14" />
                </svg>
              </div>
              <span className="info-label">{formatTranslation('recodeLabels.details.statusLabel') || 'Status'}</span>
              <span className="info-separator">:</span>
              <span className="info-value">{isActive ? 'Active' : 'Inactive'}</span>
            </div>

            {/* Related Songs Count Row */}
            <div className="info-row">
              <div className="info-icon-wrapper">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M9 18V5l12-2v13" />
                  <circle cx="6" cy="18" r="3" />
                  <circle cx="18" cy="16" r="3" />
                </svg>
              </div>
              <span className="info-label">{formatTranslation('recodeLabels.details.relatedSongCountLabel') || 'Related Song Count'}</span>
              <span className="info-separator">:</span>
              <span className="info-value">{formatTranslation('recodeLabels.details.relatedSongCountValue', { count: labelItem.songCount || 0 }) || `${labelItem.songCount || 0} Songs`}</span>
            </div>
          </div>

          {/* Action buttons footer aligned right */}
          <div className="basic-details-actions-footer" style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '28px', paddingTop: '16px', borderTop: '1px solid #f1f5f9' }}>
            {isActive ? (
              <>
                <button 
                  type="button" 
                  onClick={() => setIsEditing(true)}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '10px 20px',
                    borderRadius: '8px',
                    border: '1px solid var(--border-color, #cbd5e1)',
                    background: '#ffffff',
                    color: '#0f172a',
                    fontWeight: '600',
                    cursor: 'pointer',
                    fontSize: '14px',
                    transition: 'all 0.15s ease'
                  }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                    <path d="M18.5 2.5a2.121 2.121 0 1 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                  </svg>
                  <span>{t('recodeLabels.details.btnEdit') || 'Edit'}</span>
                </button>

                <button 
                  type="button" 
                  onClick={() => onInactivate && onInactivate(labelItem)}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '10px 20px',
                    borderRadius: '8px',
                    border: 'none',
                    background: '#fef3c7',
                    color: '#d97706',
                    fontWeight: '600',
                    cursor: 'pointer',
                    fontSize: '14px',
                    transition: 'all 0.15s ease'
                  }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <circle cx="12" cy="12" r="10" />
                    <line x1="8" y1="12" x2="16" y2="12" />
                  </svg>
                  <span>{t('recodeLabels.details.btnInactive') || 'Inactive'}</span>
                </button>
              </>
            ) : (
              <>
                <button 
                  type="button" 
                  onClick={() => onActivate && onActivate(labelItem)}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '10px 20px',
                    borderRadius: '8px',
                    border: 'none',
                    background: '#dcfce7',
                    color: '#16a34a',
                    fontWeight: '600',
                    cursor: 'pointer',
                    fontSize: '14px',
                    transition: 'all 0.15s ease'
                  }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                    <polyline points="22 4 12 14.01 9 11.01" />
                  </svg>
                  <span>{t('recodeLabels.details.btnReactivate') || 'Reactivate'}</span>
                </button>

                <button 
                  type="button" 
                  onClick={() => onDelete && onDelete(labelItem)}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '10px 20px',
                    borderRadius: '8px',
                    border: 'none',
                    background: '#fee2e2',
                    color: '#ef4444',
                    fontWeight: '600',
                    cursor: 'pointer',
                    fontSize: '14px',
                    transition: 'all 0.15s ease'
                  }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <polyline points="3 6 5 6 21 6" />
                    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                  </svg>
                  <span>{t('recodeLabels.details.btnDelete') || 'Delete'}</span>
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {/* Songs tab content panel */}
      {activeTab === 'songs' && (
        <div className="songs-list-tab-content">
          <SharedHeader
            title=""
            searchQuery={songSearchQuery}
            onSearchChange={setSongSearchQuery}
            searchPlaceholder={t('songs.searchPlaceholder') || 'Search songs...'}
            resultCount={songsTotalCount}
            resultLabel="Songs"
            onSortClick={null}
            onFilterClick={null}
            onExportClick={handleExportSongs}
            onAddNewClick={null}
            addNewText={null}
          />

          <div 
            className="song-table-container" 
            onScroll={handleSongsScroll}
            style={{ marginTop: '16px', maxHeight: '550px', overflowY: 'auto' }}
          >
            <div className="song-table-header song-table-grid recode-songs-table-grid">
              <div>{formatTranslation('recodeLabels.details.songNameLabel') || 'Song Name'}</div>
              <div>{formatTranslation('recodeLabels.details.statusLabel') || 'Status'}</div>
              <div>{formatTranslation('recodeLabels.details.artistLabel') || 'Artist'}</div>
              <div>{formatTranslation('recodeLabels.details.lyricsMusicLabel') || 'Lyrics & Music'}</div>
              <div>{formatTranslation('recodeLabels.details.labelsLabel') || 'Labels'}</div>
              <div>{formatTranslation('recodeLabels.details.ownershipLabel') || 'Ownership'}</div>
              <div>{formatTranslation('recodeLabels.details.notesCasesLabel') || 'Notes / Cases'}</div>
              <div>{formatTranslation('recodeLabels.details.conflictsLabel') || 'Conflicts'}</div>
            </div>

            <div className="song-table-body">
              {isSongsLoading ? (
                <Loader label={formatTranslation('recodeLabels.details.fetchingSongs') || 'Fetching songs, please wait.'} />
              ) : filteredSongs.length > 0 ? (
                filteredSongs.map((song) => (
                  <div 
                    key={song.id} 
                    className="song-row song-table-grid recode-songs-table-grid"
                    onClick={() => onSelectSong && onSelectSong(song)}
                    style={{ cursor: 'pointer' }}
                  >
                    <div>
                      <div className="bold-text">{song.name}</div>
                      <div style={{ fontSize: '11px', color: '#64748b' }}>{song.isrcCode || 'Song Name'}</div>
                    </div>
                    <div>
                      <span className="status-pill active">
                        <span className="status-dot"></span>
                        {song.status}
                      </span>
                    </div>
                    <div>
                      <div className="bold-text">{song.artist}</div>
                      <div style={{ fontSize: '11px', color: '#64748b' }}>{song.versionType}</div>
                    </div>
                    <div>
                      <div className="bold-text">{song.lyrics}</div>
                      <div style={{ fontSize: '11px', color: '#64748b' }}>{song.music}</div>
                    </div>
                    <div>
                      <LabelAvatarStack labels={song.labels || song.recordLabels} />
                    </div>
                    <div>{song.ownership !== undefined ? song.ownership : (song.ownershipPercentage !== undefined ? song.ownershipPercentage : 0)}%</div>
                    <div>{song.notes || formatTranslation('recodeLabels.details.noCasesOrNotes') || 'No Cases Or Notes'}</div>
                    <div>
                      <span className={`conflicts-badge ${song.conflictCount > 0 || (song.conflict && song.conflict !== 'No' && song.conflict !== '0') ? 'conflict' : 'no-conflict'}`}>
                        {song.conflicts || song.conflict || 'No'}
                      </span>
                    </div>
                  </div>
                ))
              ) : (
                <EmptyState title={formatTranslation('recodeLabels.details.noSongsFound') || 'No Songs Found'} subtitle={formatTranslation('recodeLabels.details.noSongsFoundSubtitle') || 'This record label has no songs associated yet.'} />
              )}
            </div>
          </div>

          {/* Pagination Row */}
          {songsTotalCount > 0 && (
            <div className="ringtone-pagination-row">
              <span className="pagination-range-text">
                {(songsCurrentPage - 1) * 20 + 1}-{Math.min(songsCurrentPage * 20, songsTotalCount)} of {songsTotalCount}
              </span>
              <div className="pagination-controls-wrapper">
                <button 
                  type="button" 
                  className="pager-btn outlined"
                  disabled={songsCurrentPage === 1}
                  onClick={() => setSongsCurrentPage(1)}
                >
                  &lt;&lt;
                </button>
                <button 
                  type="button" 
                  className="pager-btn outlined"
                  disabled={songsCurrentPage === 1}
                  onClick={() => setSongsCurrentPage(songsCurrentPage - 1)}
                >
                  &lt;
                </button>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map(num => (
                  <button 
                    key={num}
                    type="button" 
                    className={`pager-btn ${songsCurrentPage === num ? 'active' : 'outlined'}`}
                    onClick={() => setSongsCurrentPage(num)}
                  >
                    {num}
                  </button>
                ))}
                <button 
                  type="button" 
                  className="pager-btn outlined"
                  disabled={songsCurrentPage === totalPages}
                  onClick={() => setSongsCurrentPage(songsCurrentPage + 1)}
                >
                  &gt;
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Cases and Notes tab content panel */}
      {activeTab === 'cases_notes' && (
        <div className="cases-notes-tab-panel" style={{ textAlign: 'left' }}>
          <div className="tab-panel-header-row">
            <span className="panel-header-title">{formatTranslation('recodeLabels.details.relatedCasesTitle') || 'Related Cases and Notes'}</span>
          </div>
          <div className="notes-cases-cards-container">
            <div className="note-case-card" style={{ minHeight: '150px' }}>
              <div>
                <h4 className="card-item-name">Notice #101</h4>
                <div className="card-item-tags">Ransilu Music, payout</div>
                <div className="card-item-situation" style={{ marginTop: '8px' }}>Status: Active</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Loading overlay for export */}
      <LoadingOverlay isLoading={isExportingSongs} message={t('common.exportingMessage') || 'Exporting data...'} />
    </div>
  );
}

export default LabelDetails;
